import { useEnum } from "@/api/services/global/enum";
import { useGetRegionList } from "@/api/services/global/master";
import { TGetAnyTimeDocumentListOutputDto } from "@/api/services/main/any-time/dtos/get-any-time-document-list.dto";
import { Button } from "@/components/elements/Button";
import { DocumentStatusTag } from "@/components/elements/DocumentStatusTag";
import { TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { AnyTimeDocumentTypeOptions } from "@/features/anytime/constants/enums";
import { AppRoutes } from "@/routes/config";
import { DOCUMENT_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export const useDocumentListColumns = () => {
  const { data: genderOptions } = useEnum("EGender");
  const { nationalityWithoutJapanOptions } = useGetRegionList();

  const columns = useMemo<MRT_ColumnDef<TGetAnyTimeDocumentListOutputDto>[]>(
    () => [
      {
        header: "書類の種類",
        Header: <TableSorting name="catd.anyTimeDocumentType">書類の種類</TableSorting>,
        Cell: ({ row }) => {
          const { anyTimeDocumentType } = row.original;
          return findLabelByValue(AnyTimeDocumentTypeOptions, anyTimeDocumentType) || EMPTY_STR.TEXT;
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "外国人名",
        Header: <TableSorting name="employee.employeeFullName">外国人名</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack gap={0.5}>
              {row.original.employees.map((employee) => (
                <Typography variant="body14">{truncateWithEllipsis(employee.employeeFullName, 26) || EMPTY_STR.TEXT}</Typography>
              ))}
            </Stack>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "呼び名",
        Header: <TableSorting name="employee.employeeNickName">呼び名</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack gap={0.5}>
              {row.original.employees.map((employee) => (
                <Typography variant="body14">
                  {employee.employeeNickName ? truncateWithEllipsis(employee.employeeNickName, 26) : EMPTY_STR.TEXT}
                </Typography>
              ))}
            </Stack>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "国籍",
        Header: <TableSorting name="employee.employeeNationalityId">国籍</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack gap={0.5}>
              {row.original.employees.map((employee) => (
                <Typography variant="body14">
                  {findLabelByValue(nationalityWithoutJapanOptions, employee.employeeNationalityId) || EMPTY_STR.TEXT}
                </Typography>
              ))}
            </Stack>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "書類作成状況",
        Header: <TableSorting name="catd.status">書類作成状況</TableSorting>,
        Cell: ({ row }) => {
          return <DocumentStatusTag status={row.original.status} />;
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "最終更新日",
        Header: <TableSorting name="catd.updatedAt">最終更新日</TableSorting>,
        Cell: ({ row }) => {
          return row.original.updatedAt ? dayjs(row.original.updatedAt).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "確認／編集",
        id: "action",
        size: 80,
        Cell: ({ row }) => {
          const { id, status } = row.original;
          return (
            <Button variant="outline" size="sm" component={Link} to={getDynamicRoute(AppRoutes.anytimeDocument, { id })}>
              {status === DOCUMENT_STATUS.IN_PROGRESS ? "編集" : "確認"}
            </Button>
          );
        },
      },
    ],
    [nationalityWithoutJapanOptions, genderOptions],
  );

  return columns;
};
